import { useAuth } from '../../../lib/auth';
import AuthLayout from '../../layouts/AuthLayout';
import clsx from 'clsx';
import { Button } from '../../../components/buttons/Button.tsx';
import { cn } from '../../../lib/helpers.ts';
import { UilCheck } from '@iconscout/react-unicons';
import api from '../../../lib/api.ts';
import { useState } from 'react';
import Alert from '../../../components/Alert.tsx';
import { useNavigate } from 'react-router-dom';

export default function TryLicensePage() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const { user, userHasFullLicense } = useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'BuyLicensePage',
    });

    const navigate = useNavigate();

    const handleActivateTrial = () => {
        console.log('activate trial');
        setMessage(null);
        setIsSubmitting(true);
        api.post('/institution/buy/trial-version')
            .then((res) => {
                setIsSuccess(true);
                setMessage(res?.data);

                setTimeout(() => {
                    navigate('/dashboard');
                }, 3000);
            })
            .catch((err) => {
                setIsSuccess(false);
                setMessage(err?.response?.data);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <AuthLayout>
            <main
                className={clsx(
                    'relative',
                    'mx-auto box-content py-9',
                    'max-w-2xl xl:max-w-3xl',
                    'px-4 sm:px-8 md:px-10'
                )}
            >
                <div
                    className={cn(
                        'flex w-full flex-col gap-12 pb-32',
                        'max-w-3xl grid-cols-1 xl:max-w-none xl:grid-cols-2',
                        'xl:grid-cols-[1fr_420px]'
                    )}
                >
                    <div className="text-center">
                        <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-product-100 text-product-500">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-sparkles text-primary h-10 w-10"
                            >
                                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
                                <path d="M20 3v4"></path>
                                <path d="M22 5h-4"></path>
                                <path d="M4 17v2"></path>
                                <path d="M5 18H3"></path>
                            </svg>
                        </div>
                        <h1>Aktivujte 14denní zkušební verzi </h1>
                        <p className="text-lg text-ink-800">
                            Vyzkoušejte Všímavec zdarma po dobu 14 dní
                        </p>
                    </div>

                    <div className="rounded-lg bg-white shadow-sm">
                        <div className="flex flex-col space-y-1.5 p-6">
                            <h3>Co zahrnuje zkušební verze?</h3>
                            <p className="text-sm text-ink-800">
                                Plný přístup ke všem funkcím bez jakýchkoliv
                                omezení
                            </p>
                        </div>
                        <div className="space-y-6 p-6 pt-0">
                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <UilCheck
                                        size={24}
                                        className="text-turquoise-500"
                                    />
                                    <span className="text-foreground">
                                        Neomezený přístup ke všem 100+ cvičením
                                    </span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <UilCheck
                                        size={24}
                                        className="text-turquoise-500"
                                    />
                                    <span className="text-foreground">
                                        Všechny kategorie a tematické celky
                                    </span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <UilCheck
                                        size={24}
                                        className="text-turquoise-500"
                                    />
                                    <span className="text-foreground">
                                        Metodické materiály pro učitele
                                    </span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <UilCheck
                                        size={24}
                                        className="text-turquoise-500"
                                    />
                                    <span className="text-foreground">
                                        Technická podpora během celé doby trvání
                                    </span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <UilCheck
                                        size={24}
                                        className="text-turquoise-500"
                                    />
                                    <span className="text-foreground">
                                        Žádné platební údaje nejsou potřeba
                                    </span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <UilCheck
                                        size={24}
                                        className="text-turquoise-500"
                                    />
                                    <span className="text-foreground">
                                        Automatické ukončení po 14 dnech bez
                                        závazků
                                    </span>
                                </div>
                            </div>
                            <div className="rounded-lg bg-product-100/50 p-4">
                                <p className="text-sm text-ink-800">
                                    Po uplynutí zkušební doby se aplikace
                                    automaticky přepne zpět do demo režimu.
                                    Pokud budete chtít pokračovat, můžete si
                                    zakoupit plnou verzi.
                                </p>
                            </div>

                            {message && (
                                <Alert type={isSuccess ? 'success' : 'error'} children={message} />
                            )}

                            {userHasFullLicense && (
                                <Button
                                    disabled
                                    color="product"
                                    className="w-full bg-product-500/50"
                                >
                                    Už máte plnou verzi aktivní
                                </Button>
                            )}
                            {user?.institution.demo_requested_at && (
                                <Button
                                    disabled
                                    color="turquoise"
                                    className="w-full bg-product-500/50"
                                >
                                    Zkušební verze již byla využita
                                </Button>
                            )}
                            {!userHasFullLicense &&
                                !user?.institution.demo_requested_at && (
                                    <Button
                                        type="button"
                                        color="product"
                                        className="w-full"
                                        disabled={isSubmitting}
                                        onClick={handleActivateTrial}
                                    >
                                        Aktivovat zkušební verzi nyní
                                    </Button>
                                )}
                        </div>
                    </div>
                </div>
            </main>
        </AuthLayout>
    );
}
